<template>
  <MoleculesModal custom-width="max-w-[378px] md:max-w-[628px]" :on-close="props.onClose" :should-show-modal="props.isOpen">
    <div class="mx-auto text-center md:px-[16px]">
      <h3 class="mb-6 mt-4 text-xl font-normal tracking-tight md:mt-8 md:text-2xl">Option Details</h3>
      <div class="main-image mx-auto mb-3 md:mb-4">
        <template v-for="(img, i) in imageUrls" :key="'main' + i">
          <transition name="fade">
            <div v-show="isSelected(i)" class="main-image__item">
              <OptimizedImage alt="ポップアップ画像" :class="['main-image__img']" :image-url="img" :optimized-image-options="{ width: 1280 }" />
            </div>
          </transition>
        </template>
        <div class="main-image__btn-area">
          <a class="prev-btn-area" @click.prevent="onClickPrev()">
            <div class="prev-btn bg-black-transparent-60">
              <img class="prev-btn__img" src="@/assets/icon/left/white.svg" />
            </div>
          </a>
          <a class="next-btn-area" @click.prevent="onClickNext()">
            <div class="next-btn bg-black-transparent-60">
              <img class="next-btn__img" src="@/assets/icon/right/white.svg" />
            </div>
          </a>
        </div>
      </div>
      <div class="sub-image-container mx-auto mb-4">
        <div class="sub-image-list">
          <template v-for="(img, i) in imageUrls" :key="'sub' + i">
            <div class="sub-image-item" @click="onClickSubImage(i)">
              <OptimizedImage alt="" class="sub-image" :image-url="img" :optimized-image-options="{ width: 240 }" />
              <div class="sub-image-item__filter hover:bg-white-transparent-40" :class="{ 'border-2 border-solid border-gold1 bg-white-transparent-40': isSelected(i) }" />
            </div>
          </template>
        </div>
      </div>
      <div class="mb-8 text-left">
        <p class="mb-3 text-lg">{{ props.experienceOption.name }}</p>
        <div class="whitespace-pre-line">
          {{ props.experienceOption.detailedDescription }}
        </div>
      </div>
      <div class="mb-8 flex justify-between md:justify-end">
        <AtomsParagraph5 class="mr-6 text-gold1">{{ formattedPrice }}</AtomsParagraph5>
        <div class="flex w-[146px] justify-center rounded-sm border-[0.5px] border-gold1">
          <button
            class="flex items-center justify-center"
            type="button"
            :class="props.disabled ? 'cursor-not-allowed opacity-40' : 'hover:opacity-90 active:opacity-80'"
            :disabled="props.disabled"
            @click.prevent="emits('decrement')"
          >
            <img src="@/assets/img/minus.svg" />
          </button>
          <AtomsParagraph4 class="flex flex-1 items-center justify-center">
            <span>{{ props.count }}</span>
          </AtomsParagraph4>
          <button
            class="flex items-center justify-center"
            data-testid="detail-incrementButton"
            type="button"
            :class="props.disabled ? 'cursor-not-allowed opacity-40' : 'hover:opacity-90 active:opacity-80'"
            :disabled="props.disabled"
            @click.prevent="emits('increment')"
          >
            <img src="@/assets/img/plus.svg" />
          </button>
        </div>
      </div>
      <div v-show="props.experienceOption.annotation" class="mb-8 bg-beige1 px-3 py-2 md:px-5 md:py-4">
        <p class="whitespace-pre-line break-words text-left text-sm leading-5">{{ props.experienceOption.annotation }}</p>
      </div>
      <div class="mx-auto w-full xs:w-[270px]" data-testid="detail-ok-button">
        <AtomsPrimaryButton :on-click="props.onClose">OK</AtomsPrimaryButton>
      </div>
    </div>
  </MoleculesModal>
</template>

<script lang="ts" setup>
import type { WabunkaExperienceEventOptionViewModel } from '@/models/wabunka/wabunkaExperienceEventOption';
import type { WabunkaExperienceOptionViewModel } from '@/models/wabunka/wabunkaExperienceOption';
import { OptimizedImage } from '@/wabunka-src/common/components';
interface Props {
  disabled?: boolean;
  onClose: () => void;
  isOpen: boolean;
  experienceOption: WabunkaExperienceEventOptionViewModel | WabunkaExperienceOptionViewModel;
  count: number;
  isPublicPrice: boolean;
}

const props = defineProps<Props>();

const emits = defineEmits(['increment', 'decrement']);
const selectedImageIndex = ref<number>(0);
const imageUrls: string[] = props.experienceOption.detailedImages.map((detailedImage) => detailedImage.imageFilepath);

const switchMainImage = async (index: number) => {
  selectedImageIndex.value = index;
};

const onClickSubImage = (index: number) => {
  switchMainImage(index);
};

const isSelected = (index: number): boolean => {
  return selectedImageIndex.value === index;
};

const onClickPrev = () => {
  if (selectedImageIndex.value === 0) {
    const maxLength = imageUrls.length - 1;
    const index = maxLength;
    switchMainImage(index);
    return;
  }
  const index = selectedImageIndex.value - 1;
  switchMainImage(index);
};

const onClickNext = () => {
  const maxLength = imageUrls.length - 1;
  if (selectedImageIndex.value === maxLength) {
    switchMainImage(0);
    return;
  }
  const index = selectedImageIndex.value + 1;
  switchMainImage(index);
};

const formattedPrice = computed(() => {
  if (props.isPublicPrice) {
    return `JPY ${props.experienceOption.priceIncludingTax.toLocaleString()}`;
  }
});
</script>

<style lang="scss" scoped>
.main-image {
  width: 100%;
  position: relative;

  // 疑似要素で横幅からアスペクト比の高さを確保する
  &:before {
    content: '';
    display: block;
    padding-top: 66.66%; /* アスペクト比率 3:2 = 100:66.66 */
  }

  &__item {
    position: absolute;
    top: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
  }
  // OptimizedImage の内部 img へ届かせるため :deep を使う
  :deep(.main-image__img) {
    width: 100%;
  }

  &__btn-area {
    position: absolute;
    top: 0;
    height: 100%;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
}
.prev-btn-area {
  width: 40%;
  height: 80%;
  position: relative;
  cursor: pointer;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
}
.prev-btn {
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  position: absolute;
  top: 48%;

  // remove button style
  border-color: transparent;
  &:focus {
    outline: none;
  }

  height: 32px;
  width: 32px;

  @apply md:h-[46px] md:w-[46px];

  &__img {
    height: 20px;
    width: 10px;

    @apply md:h-[26px] md:w-[13px];
  }
}
.next-btn-area {
  width: 40%;
  height: 80%;
  position: relative;
  cursor: pointer;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
}
.next-btn {
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  position: absolute;
  top: 48%;
  right: 0;

  // remove button style
  border-color: transparent;
  &:focus {
    outline: none;
  }

  height: 32px;
  width: 32px;

  @apply md:h-[46px] md:w-[46px];

  &__img {
    height: 20px;
    width: 10px;
    z-index: 1;

    @apply md:h-[26px] md:w-[13px];
  }
}

.sub-image-container {
  width: 100%;

  // scroll設定
  overflow-x: scroll;
  &::-webkit-scrollbar {
    height: 2px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: white;
    border-radius: 10px;
  }
  &::-webkit-scrollbar-track {
    background-color: transparent;
  }
  // firefox用
  scrollbar-color: transparent white;
}

.sub-image-list {
  display: flex;
  align-items: flex-start;
  flex-wrap: nowrap;
  padding-bottom: 16px;
}

.sub-image-item {
  position: relative;
  flex-shrink: 0;
  width: 56.92px;
  height: auto;
  &:nth-child(n - 1) {
    margin-right: 8px;
  }

  @apply md:[&:nth-child(n-1)]:mr-4;

  &__filter {
    position: absolute;
    top: 0;
    width: 100%;
    height: 100%;
  }
}

.image-above-area {
  position: absolute;
  top: -24px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;

  @apply md:top-[-32px];
}

.image-index {
  font-family: 'Noto Sans JP', sans-serif;
  color: white;
  font-size: 14px;
  line-height: 14px;
  letter-spacing: 0;
  margin-left: 16px;

  @apply md:ml-0;
}

.close-btn {
  cursor: pointer;
  margin-right: 16px;
  width: 12px;
  height: 12px;
  display: flex;
  justify-content: center;
  align-items: center;

  @apply md:mr-0 md:h-4 md:w-4;
}
</style>
